/**
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * configures two of its actions for the theme's cart:
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, reads the cart and
 *     publishes `cart-update`; the cart page, the cart drawer and the
 *     header's cart count render themselves from it.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// After a Storefront API mutation, publish 'cart-update' so the subscribers run.
//
// The cart is read first so that `cartData` is defined for them: price-per-item.js
// reads its items to update the volume price, and cart-shake.js its
// item_count to tell whether the cart grew. Both cope without it.
async function refreshCartUI() {
  const cartData = await sharedCart.read();

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // The theme listens for no shopify:cart:* event, but the bundle
    // requires an eventTarget: the document.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshCartUI();
      } catch (error) {
        console.warn('Core: the cart could not be refreshed after a standard action; reloading', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
